import type { DrawStatusTone } from './InvoiceDeliveryTableWithStripePayment.styles'
import {
  InvoiceDeliveryItemDto,
  InvoicePaymentAttemptDto,
  InvoicePaymentType,
  StripePaymentMethodDto,
} from '../../api/dto/invoice-delivery.dto'
import { formatStatusLabel } from './generation/utils'

export interface DrawStatusView {
  label: string
  tone: DrawStatusTone
  /** Count of attempts in `paymentList` with status `succeeded`. */
  succeededCount: number
  /** Total attempts in `paymentList`. Zero when there is no payment list. */
  totalCount: number
}

/*
 * Shared vocabulary for a Stripe draw status, whether it's the invoice-level
 * `overallStatus` or a single `paymentList` attempt's `paymentStatus`. Backend
 * owns the actual strings — these sets classify them into the tones the UI
 * renders, and any value outside every set below still renders (title-cased)
 * rather than disappearing.
 */
export const SUCCEEDED_STATUS = 'succeeded'
export const NOT_STARTED_STATUS = 'not_started'
export const FAILED_STATUS = 'failed'

/* A draw that ended badly, shown in red. `canceled`/`cancelled` are retryable
 * via the Draw button; `failed` is not — see `canDrawDelivery`. */
export const FAILED_STATUSES = new Set([FAILED_STATUS, 'canceled', 'cancelled'])

/* A draw still in flight — never offer a second one on top of it. */
export const IN_PROGRESS_STATUSES = new Set([
  'pending',
  'processing',
  'requires_action',
  'requires_capture',
  'requires_confirmation',
  'requires_payment_method',
])

export const resolveStatusTone = (status: string): DrawStatusTone => {
  if (status === SUCCEEDED_STATUS) return 'success'
  if (FAILED_STATUSES.has(status)) return 'danger'
  if (IN_PROGRESS_STATUSES.has(status)) return 'progress'
  return 'neutral'
}

/*
 * Stripe webhook event types this app cares about, keyed by the
 * `payment_intent.*` suffix.
 */
const STRIPE_EVENT_DISPLAY: Record<
  string,
  { label: string; tone: DrawStatusTone }
> = {
  created: { label: 'Created', tone: 'neutral' },
  processing: { label: 'Processing', tone: 'progress' },
  succeeded: { label: 'Succeeded', tone: 'success' },
  payment_failed: { label: 'Failed', tone: 'danger' },
  canceled: { label: 'Canceled', tone: 'danger' },
  requires_action: { label: 'Requires action', tone: 'progress' },
  requires_capture: { label: 'Requires capture', tone: 'progress' },
  requires_confirmation: {
    label: 'Requires confirmation',
    tone: 'progress',
  },
  requires_payment_method: {
    label: 'Requires payment method',
    tone: 'progress',
  },
  amount_capturable_updated: {
    label: 'Amount capturable updated',
    tone: 'progress',
  },
  partially_funded: { label: 'Partially funded', tone: 'progress' },
}

/*
 * Unrecognised Stripe event types still render, humanized, rather than
 * disappearing — same philosophy as `resolveDrawStatus` for statuses the
 * backend hasn't been taught about yet.
 */
const humanizeStripeEventType = (eventType: string): string =>
  eventType
    .split(/[._]/)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ')

export const resolveStripeEventDisplay = (
  eventType: string
): { label: string; tone: DrawStatusTone } => {
  const suffix = eventType.split('.').pop() ?? eventType
  return (
    STRIPE_EVENT_DISPLAY[suffix] ?? {
      label: humanizeStripeEventType(eventType),
      tone: 'neutral',
    }
  )
}

/*
 * Names a payment attempt by the bank account that was (or would be) drawn
 * from — there's no per-attempt company name on `paymentList`. Falls back
 * when the first payment method isn't a bank account (e.g. a card) or hasn't
 * been attached yet.
 */
const formatBankAccountLabel = (
  method: StripePaymentMethodDto | null | undefined
): string | null => {
  const bankAccount = method?.us_bank_account
  if (!bankAccount?.bank_name) return null
  return `${bankAccount.bank_name} (***${bankAccount.last4 ?? ''})`
}

export const resolveBankDetailLabel = (
  attempt: InvoicePaymentAttemptDto
): string | null => formatBankAccountLabel(attempt.stripePaymentMethods?.[0])

/*
 * The payment method a failed attempt is retried against: the customer's
 * default when set, otherwise the first attached method. Null when the
 * customer has none, in which case there is nothing to retry with.
 */
export const resolveRetryPaymentMethodId = (
  attempt: InvoicePaymentAttemptDto
): string | null =>
  attempt.defaultStripePaymentMethodId ??
  attempt.stripePaymentMethods?.[0]?.id ??
  null

/*
 * Names the bank a retry will actually charge — which may not be the first
 * method `resolveBankDetailLabel` shows. Null when that method isn't in the
 * attached list or isn't a bank account, so the confirmation never names the
 * wrong bank.
 */
export const resolveRetryBankDetailLabel = (
  attempt: InvoicePaymentAttemptDto
): string | null => {
  const paymentMethodId = resolveRetryPaymentMethodId(attempt)
  return formatBankAccountLabel(
    attempt.stripePaymentMethods?.find(
      (method) => method.id === paymentMethodId
    )
  )
}

/* A failed attempt can be retried only when there is an intent and a method to charge. */
export const canRetryPaymentAttempt = (
  attempt: InvoicePaymentAttemptDto
): boolean =>
  attempt.paymentStatus === FAILED_STATUS &&
  !!attempt.stripePaymentIntentId &&
  resolveRetryPaymentMethodId(attempt) !== null

/*
 * A `paymentList` attempt's amount comes from Stripe, in cents — everything
 * else in this app (invoiceMetadata.totalDue, user-entered draw amounts) is
 * plain dollars. Convert at the boundary rather than carrying cents into
 * display/validation code that assumes dollars everywhere else.
 */
export const centsToDollars = (cents: number): number => cents / 100
export const dollarsToCents = (dollars: number): number =>
  Math.round(dollars * 100)

const isFiniteAmount = (value: number | null | undefined): value is number =>
  typeof value === 'number' && Number.isFinite(value)

/*
 * The backend has been observed sending payment type in inconsistent case
 * (e.g. "Stripe" rather than "STRIPE"), which silently failed every
 * `InvoicePaymentType` comparison — the Draw button read as "This client
 * does not pay by Stripe" for a client that does. Normalize once at read
 * time instead of patching every comparison site.
 */
export const normalizePaymentType = (
  value: string | null | undefined
): InvoicePaymentType | null => {
  const upper = value?.toUpperCase()
  if (upper === InvoicePaymentType.WIRE) return InvoicePaymentType.WIRE
  if (upper === InvoicePaymentType.STRIPE) return InvoicePaymentType.STRIPE
  return null
}

/*
 * A wire client settles outside Stripe, so a draw can never apply to it —
 * the button is hidden rather than disabled. Archived and historical rows
 * are the same: they are not a thing an operator can ever draw against, so
 * they show no draw control. Mirrors shouldShowSendAction.
 */
export const shouldShowDrawAction = (record: InvoiceDeliveryItemDto): boolean =>
  normalizePaymentType(record.paymentType) !== InvoicePaymentType.WIRE &&
  record.isLatest &&
  !record.isArchived

export const canDrawDelivery = (
  record: InvoiceDeliveryItemDto,
  paymentType: InvoicePaymentType | null
): boolean => {
  const overallStatus = record.paymentDetail?.overallStatus
  const isRetryable =
    !overallStatus ||
    overallStatus === NOT_STARTED_STATUS ||
    (FAILED_STATUSES.has(overallStatus) && overallStatus !== FAILED_STATUS)
  return (
    record.isLatest &&
    !record.isArchived &&
    paymentType === InvoicePaymentType.STRIPE &&
    !!record.paymentDetail &&
    isFiniteAmount(record.invoiceMetadata?.totalDue) &&
    isRetryable
  )
}

export const resolveDrawDisabledReason = (
  record: InvoiceDeliveryItemDto,
  paymentType: InvoicePaymentType | null
): string => {
  const overallStatus = record.paymentDetail?.overallStatus
  if (paymentType === null)
    return 'This client has no billing profile, so no payment type is set.'
  if (paymentType !== InvoicePaymentType.STRIPE)
    return 'This client does not pay by Stripe.'
  if (record.isArchived) return 'Unarchive this delivery before drawing'
  if (!record.isLatest) return 'Only the latest version can be drawn'
  if (!record.paymentDetail)
    return 'This client has no Stripe account connected to draw from.'
  if (overallStatus === SUCCEEDED_STATUS)
    return 'This invoice has already been paid.'
  if (overallStatus === FAILED_STATUS)
    return 'This draw failed — draw from Stripe directly to retry it.'
  if (overallStatus && IN_PROGRESS_STATUSES.has(overallStatus))
    return 'A draw is already in progress.'
  if (
    overallStatus &&
    overallStatus !== NOT_STARTED_STATUS &&
    !FAILED_STATUSES.has(overallStatus)
  )
    return `Draw status is "${formatStatusLabel(overallStatus)}" — resolve it in Stripe first.`
  if (!isFiniteAmount(record.invoiceMetadata?.totalDue))
    return 'This invoice has no readable Total Due to draw.'
  return ''
}

/*
 * How many of the invoice's draw attempts succeeded, out of how many were
 * made. Absent/empty `paymentList` reports 0/0, which callers can skip.
 */
export const resolvePaymentListCounts = (
  record: InvoiceDeliveryItemDto
): Pick<DrawStatusView, 'succeededCount' | 'totalCount'> => {
  const paymentList = record.paymentDetail?.paymentList ?? []
  return {
    succeededCount: paymentList.filter(
      (p) => p.paymentStatus === SUCCEEDED_STATUS
    ).length,
    totalCount: paymentList.length,
  }
}

/*
 * Null means "render a dash" rather than a badge. Any status the backend adds
 * later still renders, title-cased, rather than disappearing from the column.
 */
export const resolveDrawStatus = (
  record: InvoiceDeliveryItemDto
): DrawStatusView | null => {
  const counts = resolvePaymentListCounts(record)
  if (!record.paymentDetail) {
    const paymentType = normalizePaymentType(record.paymentType)
    /*
     * A wire client with no payment activity is not waiting on a draw, so a
     * "Not Started" badge would imply a pending action that will never happen.
     * A wire row that does carry payment detail (a reconciliation, say) reports
     * exactly like any other row below.
     */
    if (paymentType === InvoicePaymentType.WIRE) return null
    /*
     * A Stripe client with no payment detail at all has never been connected
     * to a Stripe customer — distinct from "Not Started", which implies a draw
     * is simply pending. This tells the operator there is nothing to draw
     * against yet.
     */
    if (paymentType === InvoicePaymentType.STRIPE)
      return { label: 'No Stripe Account', tone: 'danger', ...counts }
    return { label: 'Not Started', tone: 'neutral', ...counts }
  }

  const overallStatus = record.paymentDetail.overallStatus
  if (!overallStatus)
    return { label: 'Not Started', tone: 'neutral', ...counts }
  const label =
    overallStatus === NOT_STARTED_STATUS
      ? 'Not Started'
      : formatStatusLabel(overallStatus)
  return { label, tone: resolveStatusTone(overallStatus), ...counts }
}

/** Same label the Drawn Status column renders, e.g. "Succeeded (2/2)". */
export const formatDrawStatusLabel = (
  record: InvoiceDeliveryItemDto
): string => {
  const drawStatus = resolveDrawStatus(record)
  if (!drawStatus) return ''
  const { label, succeededCount, totalCount } = drawStatus
  return totalCount > 0 ? `${label} (${succeededCount}/${totalCount})` : label
}
